import { ChangeEvent} from "react";
import {
  Grid,
  MenuItem,
  Paper,
  TextField,
  Stack,
  Typography,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { enGB, de, da, es, fr } from "date-fns/locale";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { Icon } from "@mui/material";
import { ActivityData, ChartData } from "../../../types/chartData";
import { Project } from "../../../types/project";
import { Formik} from "formik";
import { DatePickerField } from "../../Reuseables/Fields/DatePickerField";

type FiltersMenuProps = {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (d: Date) => void;
  setEndDate: (d: Date) => void;
  selectedProject: Project | null;
  setSelectedProject: (p: Project | null) => void;
  projects?: Project[];
  data: ChartData | null;
  selectedActivity: ChartData | ActivityData | string | null;
  selectedActivityChanged: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  isComparison?: boolean;
  attribute?: string;
  isLoading: boolean;
  setAttribute?: (s: string) => void;
  onProjectChanged: (
    sDate: Date,
    eDate: Date,
    projectCode: string
  ) => void | Promise<any>;
};

function FiltersMenu({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  selectedProject,
  setSelectedProject,
  projects,
  data,
  selectedActivity,
  selectedActivityChanged,
  isComparison,
  isLoading,
  attribute,
  setAttribute,
  onProjectChanged: onProjectChanged,
}: FiltersMenuProps) {
  const { t, i18n } = useTranslation();

  const onSubmit = (value: any, helpers: any) => {
    let { startDate, endDate, selectedProject } = value;

    if (!startDate) startDate = new Date(selectedProject.startDate1);
    if (!endDate) endDate = new Date();

    setStartDate(startDate);
    setEndDate(endDate);
    setSelectedProject(selectedProject);
    onProjectChanged(startDate, endDate, selectedProject.projectCode);
  };


  return (
    <>
      <Formik
        initialValues={{ startDate, endDate, selectedProject }}
        enableReinitialize
        onSubmit={onSubmit}
      >
        {(props) => (
          <Paper elevation={3} sx={{ padding: 2 }}>
            <Grid container flexDirection={"column"} spacing={2}>
              <Grid item xs>
                <Grid
                  container
                  spacing={1}
                  sx={{
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                  }}
                >
                  <Grid item xs={12} sm={12} md={6}>
                    <TextField
                      disabled={isLoading}
                      size="small"
                      name="selectedProject"
                      select
                      label={isComparison && t("compare-with")}
                      fullWidth
                      value={props.values.selectedProject?.projectCode || ""}
                      onChange={(e) => {
                        const selectedId = e.target.value;
               
                        if (selectedId === t("labels.none")) {
                          props.setFieldValue("selectedProject", null);
                        } else {
                          const selected = projects?.find(
                            (p) => p.projectCode === selectedId
                          );

                          props.setFieldValue("selectedProject", selected);
                        }
                      }}
                    >
                      {isComparison && (
                        <MenuItem key="none" value={t("labels.none")}>
                          {t("labels.none")}
                        </MenuItem>
                      )}

                      {projects?.map((project) => (
                        <MenuItem
                          key={project.projectCode}
                          value={project.projectCode}
                        >
                          {project.projectCode} - {project.projectName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      label={t("labels.activity")}
                      value={selectedActivity ? selectedActivity : ""}
                      onChange={selectedActivityChanged}
                      disabled={isLoading || (selectedProject ? false : true)}
                    >
                      {!data && (
                        <MenuItem value={t("overall")}>{t("overall")}</MenuItem>
                      )}
                      {data && (
                        <MenuItem
                          key={1337}
                          value={t(`${data.activities[0].activityName}`)}
                          // disabled={data.activities[0].average == null}
                        >
                          <Stack
                            sx={{ width: "100%" }}
                            direction="row"
                            justifyContent="space-between"
                          >
                            <Typography>
                              {t(`${data.activities[0].activityName}`)}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      )}
                      {data
                        ? data.activities.slice(1).map((activity, index) => (
                            <MenuItem
                              key={index}
                              value={activity.activityName}
                              disabled={activity.average == null}
                            >
                              <Stack
                                sx={{ width: "100%" }}
                                direction="row"
                                justifyContent="space-between"
                              >
                                <Typography>{activity.activityName}</Typography>
                                {activity.average == null && (
                                  <Tooltip
                                    PopperProps={{
                                      style: {
                                        pointerEvents: "none",
                                        marginTop: "40px",
                                      },
                                    }}
                                    sx={{ pointerEvents: "inherit !important" }}
                                    title={"Not enough data"}
                                    placement="right"
                                  >
                                    <Icon color={"info"} fontSize="small">
                                      <PriorityHighIcon />
                                    </Icon>
                                  </Tooltip>
                                )}
                              </Stack>
                            </MenuItem>
                          ))
                        : null}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs>
                <Grid container flexDirection={"row"} spacing={1}>
                  <Grid
                    item
                    xs={6}
                    sm={isComparison ? 6 : 4}
                    md={6}
                    lg={isComparison ? 6 : 4}
                  >
                    <DatePickerField
                      disabled={isLoading || (selectedProject ? false : true)}
                      name="startDate"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    sm={isComparison ? 6 : 4}
                    md={6}
                    lg={isComparison ? 6 : 4}
                  >
                    <DatePickerField
                      disabled={isLoading || (selectedProject ? false : true)}
                      name="endDate"
                    />
                  </Grid>
                  {!isComparison && (
                    <Grid item xs={12} sm={4} md={12} lg={4}>
                      <TextField
                        select
                        fullWidth
                        label={t("metric")}
                        value={attribute}
                        onChange={(event) => {
                          if (setAttribute) setAttribute(event.target.value);
                        }}
                        disabled={isLoading}
                      >
                        <MenuItem value="MLI">MLI</MenuItem>
                        <MenuItem value={t("pain")}>{t("pain")}</MenuItem>
                      </TextField>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end">
                <Grid item>
               
                  <Button
                    sx={{ marginTop: 1 }}
                    variant={props.dirty ? "contained" : "text"}
                    disabled={!props.dirty ? true : false}
                    onClick={props.submitForm}
                  >
                    {isLoading ? <CircularProgress size={"1.5rem"}/> :  "Apply"}
                  
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Formik>
    </>
  );
}

export default FiltersMenu;
